// src/components/AllergyBanner.tsx
// Enhanced contextual allergy banner component

import React, { useState } from 'react';
import DynamicBanner from './DynamicBanner';
import { useContextualAllergyMessages } from '@utils/allergyMessageUtil';

interface AllergyBannerProps {
  events: string[];
  recommendation?: {
    treatment1?: Array<{
      treatment: {
        titleDisplay?: string;
        antimicrobialUnit?: {
          title?: string;
        };
      };
    }>;
    treatment2?: Array<{
      treatment: {
        titleDisplay?: string;
        antimicrobialUnit?: {
          title?: string;
        };
      };
    }>;
  };
  className?: string;
  dismissible?: boolean;
  showDebugInfo?: boolean; // For development debugging
}

/**
 * Enhanced AllergyBanner component that displays contextual allergy messages
 * based on allergy codes and actual treatment recommendations.
 * Handles Sanity recommendation structure with treatment1/treatment2 arrays.
 */
export const AllergyBanner: React.FC<AllergyBannerProps> = ({ 
  events,
  recommendation = null,
  className = '',
  dismissible = false,
  showDebugInfo = false
}) => {
  const { 
    messages, 
    hasMessages, 
    treatmentContext,
    allergyCode 
  } = useContextualAllergyMessages(events, recommendation);
  
  const [isDismissed, setIsDismissed] = useState(false);

  // Don't render if no messages or dismissed
  if (!hasMessages || isDismissed) {
    // Optional debug info for development
    if (showDebugInfo && allergyCode && !hasMessages) {
      return (
        <div className={`allergy-banner debug-info ${className}`}
             style={{ padding: '8px', backgroundColor: '#f5f5f5', fontSize: 'var(--font-size-sm, 12px)', color: '#666' }}>
          <strong>Debug:</strong> Allergy code "{allergyCode}" detected but no contextual message needed.
          <br />
          <strong>Drug names found:</strong> {treatmentContext.drugNames.join(', ') || 'none'}
          <br />
          <strong>Beta-lactams detected:</strong> {treatmentContext.hasBetaLactams ? 'Yes' : 'No'}
          <br />
          <strong>Treatment context:</strong> {JSON.stringify(treatmentContext, null, 2)}
        </div>
      );
    }
    return null;
  }

  const handleClose = dismissible ? () => setIsDismissed(true) : undefined;

  return (
    <div className={`allergy-banner ${className}`}>
      {messages.map((message, index) => (
        <DynamicBanner 
          key={`${message.allergyCode}-${index}`}
          onClose={handleClose}
          backgroundColor={message.backgroundColor}
          textColor={message.textColor}
        >
          <div>
            <strong>Allergy Consideration:</strong> {message.message}
          </div>
          
          {showDebugInfo && (
            <details style={{ marginTop: '8px', fontSize: 'var(--font-size-sm, 12px)', opacity: 0.8 }}>
              <summary>Debug Info</summary>
              <pre style={{ fontSize: '10px', marginTop: '4px' }}>
                {JSON.stringify({
                  allergyCode: message.allergyCode,
                  severity: message.severity,
                  drugsDetected: message.context.drugNames,
                  classifications: message.context.classifications,
                  primaryType: message.context.primaryBetaLactamType
                }, null, 2)}
              </pre>
            </details>
          )}
        </DynamicBanner>
      ))}
    </div>
  );
};

export default AllergyBanner;